import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './Notices.css';

function Notices() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notices, setNotices] = useState([]);
  const [newNotice, setNewNotice] = useState({
    title: '',
    content: '',
    target_audience: 'students',
    priority: 'normal',
    expiry_date: '',
    notify_type: 'in_app',
    schedule_at: '',
    media_files: [],
    allow_send_now: true
  });
  const [showAddNotice, setShowAddNotice] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);

  // Chat Management state
  const [chatGroups, setChatGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [chatFiles, setChatFiles] = useState([]);
  const [cgClass, setCgClass] = useState('');
  const [cgSection, setCgSection] = useState('');
  const [cgName, setCgName] = useState('');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      (async () => {
        const { data: cls } = await supabase
          .from('classes')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('class_name');
        setClasses(cls || []);
        await fetchChatGroups();
      })();
    }
  }, [schoolData?.id]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchNotices();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchNotices = async () => {
    try {
      setNotices([]);
      // Check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        alert('Authentication error. Please log in again.');
        navigate('/schoollogin');
        return;
      }

      if (!session) {
        console.error('No active session');
        alert('Please log in to access notices.');
        navigate('/schoollogin');
        return;
      }

      // Auto-send any scheduled notices that are due (client-side simple scheduler)
      const nowIso = new Date().toISOString();
      let dueQuery = supabase
        .from('notices')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('status', 'scheduled')
        .lte('schedule_at', nowIso);

      if (selectedBranchId != null) {
        dueQuery = dueQuery.eq('branch_id', selectedBranchId);
      }

      const { data: due } = await dueQuery;
      if (due && due.length > 0) {
        for (const n of due) {
          await supabase.from('notices').update({ status: 'sent', sent_at: new Date().toISOString() }).eq('id', n.id);
          // Integration hook placeholder
          try { await triggerIntegration(n); } catch {}
        }
      }

      let query = supabase
        .from('notices')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notices:', error);
        if (error.code === 'PGRST301' || error.message.includes('JWT')) {
          alert('Authentication expired. Please log in again.');
          navigate('/schoollogin');
        } else {
          alert('Failed to fetch notices. Please check your internet connection.');
        }
        return;
      }

      setNotices(data || []);
    } catch (err) {
      console.error('Error:', err);
      if (err.message.includes('Failed to fetch') || err.message.includes('ERR_INTERNET_DISCONNECTED')) {
        alert('Network error. Please check your internet connection and try again.');
      } else {
        alert('An error occurred while fetching notices.');
      }
    }
  };

  const handleNoticeSubmit = async (e) => {
    e.preventDefault();
    
    // Check if we have a valid session before proceeding
    try {
      let createdNotice = null;
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        alert('Authentication error. Please log in again.');
        navigate('/schoollogin');
        return;
      }

      if (!session) {
        console.error('No active session');
        alert('Please log in to create notices.');
        navigate('/schoollogin');
        return;
      }

      // Upload media if provided
      let media_urls = null;
      if (newNotice.media_files && newNotice.media_files.length > 0) {
        media_urls = [];
        for (const file of newNotice.media_files) {
          const ext = file.name.split('.').pop();
          const key = `notices/${schoolData.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
          const { error: upErr } = await supabase.storage.from('student-images').upload(key, file);
          if (!upErr) {
            const { data: { publicUrl } } = supabase.storage.from('student-images').getPublicUrl(key);
            media_urls.push({ name: file.name, url: publicUrl, size: file.size });
          }
        }
      }

      if (editingNotice) {
        // Update existing notice
        const { error } = await supabase
          .from('notices')
          .update({
            title: newNotice.title,
            content: newNotice.content,
            target_audience: newNotice.target_audience,
            priority: newNotice.priority,
            expiry_date: newNotice.expiry_date || null,
            notify_type: newNotice.notify_type || 'in_app',
            schedule_at: newNotice.schedule_at || null,
            media_urls: media_urls || undefined,
          updated_at: new Date().toISOString(),
          branch_id: selectedBranchId
          })
          .eq('id', editingNotice.id);

        if (error) {
          console.error('Error updating notice:', error);
          if (error.code === 'PGRST301' || error.message.includes('JWT')) {
            alert('Authentication expired. Please log in again.');
            navigate('/schoollogin');
          } else if (error.code === '23505') {
            alert('A notice with this title already exists.');
          } else {
            alert('Failed to update notice. Please try again.');
          }
          return;
        }
      } else {
        // Create new notice
        const { data: createdData, error } = await supabase
          .from('notices')
          .insert({
            school_id: schoolData.id,
            title: newNotice.title,
            content: newNotice.content,
            target_audience: newNotice.target_audience,
            priority: newNotice.priority,
            expiry_date: newNotice.expiry_date || null,
            notify_type: newNotice.notify_type || 'in_app',
            schedule_at: newNotice.schedule_at || null,
            status: newNotice.schedule_at ? 'scheduled' : 'sent',
            sent_at: newNotice.schedule_at ? null : new Date().toISOString(),
            media_urls: media_urls,
        is_active: true,
        branch_id: selectedBranchId
          })
          .select()
          .single();
        createdNotice = createdData;

        if (error) {
          console.error('Error creating notice:', error);
          if (error.code === 'PGRST301' || error.message.includes('JWT')) {
            alert('Authentication expired. Please log in again.');
            navigate('/schoollogin');
          } else if (error.code === '42501') {
            alert('Permission denied. Please contact support to fix RLS policies.');
            console.error('RLS Policy Error - School ID:', schoolData.id, 'Auth UID:', session?.user?.id);
          } else if (error.code === '23505') {
            alert('A notice with this title already exists.');
          } else if (error.code === '23503') {
            alert('Invalid school ID. Please contact support.');
          } else {
            alert('Failed to create notice. Please try again.');
          }
          return;
        }
      }

      // Reset form
      setNewNotice({
        title: '',
        content: '',
        target_audience: 'students',
        priority: 'normal',
        expiry_date: '',
        notify_type: 'in_app',
        schedule_at: '',
        media_files: []
      });
      setShowAddNotice(false);
      setEditingNotice(null);
      
      // Refresh notices
      fetchNotices();
      alert(editingNotice ? 'Notice updated successfully!' : 'Notice created successfully!');
      // If sent now and integration chosen, trigger hook
      if (!editingNotice && createdNotice && !newNotice.schedule_at) {
        try { await triggerIntegration(createdNotice); } catch {}
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.message.includes('Failed to fetch') || err.message.includes('ERR_INTERNET_DISCONNECTED')) {
        alert('Network error. Please check your internet connection and try again.');
      } else {
        alert('An error occurred while saving the notice. Please try again.');
      }
    }
  };

  const triggerIntegration = async (notice) => {
    // Placeholder: integrate with Push/Email/SMS provider
    // You can set WEBHOOK_URL to forward payload
    const hook = process.env.REACT_APP_COMM_WEBHOOK_URL;
    const payload = { notice_id: notice.id, school_id: notice.school_id, audience: notice.target_audience, notify_type: notice.notify_type, title: notice.title, content: notice.content };
    if (hook) {
      try { await fetch(hook, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }); } catch (e) { /* ignore */ }
    } else {
      console.log('Integration hook payload', payload);
    }
  };

  // ===== Chat Management functions =====
  const fetchChatGroups = async () => {
    const { data } = await supabase
      .from('chat_groups')
      .select('*')
      .eq('school_id', schoolData.id)
      .order('created_at', { ascending: false });
    setChatGroups(data || []);
  };

  useEffect(() => {
    if (!cgClass) { setSections([]); return; }
    const c = classes.find(c => c.id === parseInt(cgClass));
    if (c?.sections) setSections(c.sections.filter(Boolean).map((s, i) => ({ id: i + 1, name: s })));
  }, [cgClass, classes]);

  const createChatGroup = async () => {
    const sectionName = sections.find(s => s.id === parseInt(cgSection))?.name || null;
    const payload = {
      school_id: schoolData.id,
      name: cgName || (cgClass ? `${classes.find(c=>c.id===parseInt(cgClass))?.class_name}${sectionName ? ' - ' + sectionName : ''}` : 'Custom Group'),
      group_type: cgClass ? 'class' : 'custom',
      class_id: cgClass ? parseInt(cgClass) : null,
      section_name: sectionName,
      created_by: 'school'
    };
    const { data, error } = await supabase.from('chat_groups').insert(payload).select().single();
    if (error) { alert('Failed to create group: ' + error.message); return; }
    setCgName(''); setCgClass(''); setCgSection('');
    setChatGroups([data, ...chatGroups]);
  };

  const openChatGroup = async (g) => {
    setActiveGroup(g);
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('school_id', schoolData.id)
      .eq('group_id', g.id)
      .order('created_at');
    setChatMessages(data || []);
  };

  const sendChatMessage = async () => {
    if (!activeGroup) return;
    let media = null;
    if (chatFiles && chatFiles.length > 0) {
      media = [];
      for (const f of chatFiles) {
        const ext = f.name.split('.').pop();
        const key = `chat/${schoolData.id}/${activeGroup.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('student-images').upload(key, f);
        if (!upErr) {
          const { data: { publicUrl } } = supabase.storage.from('student-images').getPublicUrl(key);
          media.push({ name: f.name, url: publicUrl, size: f.size });
        }
      }
    }
    const payload = {
      school_id: schoolData.id,
      group_id: activeGroup.id,
      sender_type: 'school',
      sender_id: schoolData.id.toString(),
      content: newChatMessage || null,
      media_urls: media
    };
    const { data, error } = await supabase.from('chat_messages').insert(payload).select().single();
    if (error) { alert('Failed to send message: ' + error.message); return; }
    setChatMessages([...chatMessages, data]);
    setNewChatMessage('');
    setChatFiles([]);
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setNewNotice({
      title: notice.title || '',
      content: notice.content || '',
      target_audience: notice.target_audience || 'students',
      priority: notice.priority || 'normal',
      expiry_date: notice.expiry_date || '',
      notify_type: notice.notify_type || 'in_app',
      schedule_at: notice.schedule_at || '',
      media_files: [],
      allow_send_now: true
    });
    setShowAddNotice(true);
    
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.querySelector('.add-notice-form-container');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('notices')
        .update({ is_active: false })
        .eq('id', noticeId);

      if (error) {
        console.error('Error deleting notice:', error);
        alert('Failed to delete notice. Please try again.');
        return;
      }

      fetchNotices();
      alert('Notice deleted successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete notice. Please try again.');
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'normal': return '🟢';
      default: return '🟢';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'High Priority';
      case 'medium': return 'Medium Priority';
      case 'normal': return 'Normal Priority';
      default: return 'Normal Priority';
    }
  };

  const getTargetAudienceIcon = (audience) => {
    switch (audience) {
      case 'students': return '👨‍🎓';
      case 'teachers': return '👨‍🏫';
      case 'parents': return '👨‍👩‍👧‍👦';
      default: return '📢';
    }
  };

  const getTargetAudienceLabel = (audience) => {
    switch (audience) {
      case 'students': return 'Students';
      case 'teachers': return 'Teachers';
      case 'parents': return 'Parents';
      default: return 'All';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>📢 Notices</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        <div className="branch-filter-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <span className="branch-context-text">
            {selectedBranchId
              ? 'Showing notices for the selected branch'
              : 'Showing notices from all branches'}
          </span>
        </div>

        <div className="notices-page-container">
          <div className="notices-container">
            {/* Add New Notice Header */}
            <div className="add-notice-header-card">
              <div className="add-notice-header-left">
                <span className="add-notice-header-icon">📢</span>
                <h2 className="add-notice-header-title">Add New Notice</h2>
              </div>
              <button
                onClick={() => setShowAddNotice(!showAddNotice)}
                className="add-notice-plus-btn"
                title={showAddNotice ? "Close Form" : "Add Notice"}
              >
                {showAddNotice ? '✕' : '➕'}
              </button>
            </div>

            {/* Add/Edit Notice Form */}
            {showAddNotice && (
              <div className="add-notice-form-container">
                <h3 className="add-notice-form-title">{editingNotice ? 'Edit Notice' : 'Add New Notice'}</h3>
                <form onSubmit={handleNoticeSubmit}>
                  <div className="notices-form-grid">
                    <div>
                      <label htmlFor="notice-title" className="notices-form-label">Notice Title *</label>
                      <input
                        type="text"
                        id="notice-title"
                        value={newNotice.title}
                        onChange={(e) => setNewNotice({...newNotice, title: e.target.value})}
                        placeholder="e.g., School Holiday Notice"
                        className="notices-form-input"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="target-audience" className="notices-form-label">Target Audience *</label>
                      <select
                        id="target-audience"
                        value={newNotice.target_audience}
                        onChange={(e) => setNewNotice({...newNotice, target_audience: e.target.value})}
                        className="notices-form-select"
                        required
                      >
                        <option value="students">👨‍🎓 Students</option>
                        <option value="teachers">👨‍🏫 Teachers</option>
                        <option value="parents">👨‍👩‍👧‍👦 Parents</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="priority" className="notices-form-label">Priority *</label>
                      <select
                        id="priority"
                        value={newNotice.priority}
                        onChange={(e) => setNewNotice({...newNotice, priority: e.target.value})}
                        className="notices-form-select"
                        required
                      >
                        <option value="normal">🟢 Normal Priority</option>
                        <option value="medium">🟡 Medium Priority</option>
                        <option value="high">🔴 High Priority</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="expiry-date" className="notices-form-label">Expiry Date (Optional)</label>
                      <input
                        type="date"
                        id="expiry-date"
                        value={newNotice.expiry_date}
                        onChange={(e) => setNewNotice({...newNotice, expiry_date: e.target.value})}
                        className="notices-form-input"
                      />
                    </div>

                    <div className="notices-form-textarea-wrapper">
                      <label htmlFor="notice-content" className="notices-form-label">Notice Content *</label>
                      <textarea
                        id="notice-content"
                        value={newNotice.content}
                        onChange={(e) => setNewNotice({...newNotice, content: e.target.value})}
                        placeholder="Enter the notice content here..."
                        className="notices-form-textarea"
                        required
                      />
                    </div>

                    <div className="notices-form-bottom-row">
                      <div>
                        <label className="notices-form-label">Notify Type</label>
                        <select value={newNotice.notify_type} onChange={(e)=> setNewNotice({...newNotice, notify_type: e.target.value})} className="notices-form-select">
                          <option value="in_app">In-App</option>
                        </select>
                      </div>
                      <div>
                        <label className="notices-form-label">Schedule Date & Time (optional)</label>
                        <input type="datetime-local" value={newNotice.schedule_at} onChange={(e)=> setNewNotice({...newNotice, schedule_at: e.target.value})} className="notices-form-input" />
                      </div>
                      <div>
                        <label className="notices-form-label">Attachments</label>
                        <input type="file" multiple onChange={(e)=> setNewNotice({...newNotice, media_files: Array.from(e.target.files)})} className="notices-form-input" />
                      </div>
                    </div>
                  </div>

                  <div className="notices-form-actions">
                    <button type="submit" className="notices-add-btn">
                      <span>{editingNotice ? '💾' : '➕'}</span>
                      <span>{editingNotice ? 'Update Notice' : 'Add Notice'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddNotice(false);
                        setEditingNotice(null);
                        setNewNotice({
                          title: '',
                          content: '',
                          target_audience: 'students',
                          priority: 'normal',
                          expiry_date: '',
                          notify_type: 'in_app',
                          schedule_at: '',
                          media_files: [],
                          allow_send_now: true
                        });
                      }}
                      className="notices-cancel-btn"
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* All Notices Section */}
            <div className="all-notices-header-card">
              <span className="all-notices-header-icon">📢</span>
              <h2 className="all-notices-header-title">All Notices</h2>
            </div>

            {/* Notices List */}
            {notices.length === 0 ? (
              <div className="no-notices">
                <div className="no-notices-icon">📢</div>
                <h3>No Notices Added</h3>
                <p>Start by adding notices for students, teachers, or parents.</p>
              </div>
            ) : (
              <div className="notices-grid">
                {notices.map((notice) => (
                  <div key={notice.id} className={`notice-card ${isExpired(notice.expiry_date) ? 'expired' : ''}`}>
                    <div className="notice-card-header">
                      <div className="notice-card-header-left">
                        <span className={`priority-badge ${notice.priority}`}>
                          <span>{getPriorityIcon(notice.priority)}</span>
                          <span>{getPriorityLabel(notice.priority)}</span>
                        </span>
                        <span className="audience-badge">
                          <span>{getTargetAudienceIcon(notice.target_audience)}</span>
                          <span>{getTargetAudienceLabel(notice.target_audience)}</span>
                        </span>
                      </div>
                      <div className="notice-card-header-right">
                        <span
                          onClick={() => handleEditNotice(notice)}
                          className="notice-edit-icon"
                          title="Edit Notice"
                        >
                          📝
                        </span>
                        <span
                          onClick={() => handleDeleteNotice(notice.id)}
                          className="notice-delete-icon"
                          title="Delete Notice"
                        >
                          🗑️
                        </span>
                      </div>
                    </div>
                    
                    <h4 className="notice-card-title">{notice.title}</h4>
                    <p className="notice-card-content">{notice.content}</p>

                    <div className="notice-card-footer">
                      <div className="notice-date-row">
                        <span className="notice-calendar-icon">📅</span>
                        <span>{formatDate(notice.created_at)}</span>
                      </div>
                      {notice.status && (
                        <div className="notice-status-row">
                          <span className={`notice-status-badge ${notice.status === 'sent' ? 'sent' : 'draft'}`}>
                            {notice.status === 'sent' ? 'Sent' : notice.status === 'draft' ? 'Draft' : notice.status}
                          </span>
                        </div>
                      )}
                      {notice.expiry_date && isExpired(notice.expiry_date) && (
                        <div className="notice-expiry-warning">
                          <span>⚠️</span>
                          <span>Expired: {formatDate(notice.expiry_date)}</span>
                        </div>
                      )}
                      {notice.media_urls && Array.isArray(notice.media_urls) && notice.media_urls.length > 0 && (
                        <div style={{marginTop:'8px', textAlign:'center'}}>
                          {notice.media_urls.map((m,idx)=> (
                            <a key={idx} href={m.url} target="_blank" rel="noreferrer" style={{display:'block', color:'#4361EE', fontSize:'12px', marginTop:'4px'}}>⬇️ {m.name}</a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Management */}
        {activeGroup && (
          <div className="notices-display-section" style={{marginTop:'1rem'}}>
            <div className="section-card">
              <h2>📨 Chat: {activeGroup.name}</h2>
              <div className="classes-table-container" style={{maxHeight:'360px', overflow:'auto'}}>
                {chatMessages.length === 0 ? <p style={{padding:'0.5rem'}}>No messages yet.</p> : (
                  <table className="classes-table"><tbody>
                    {chatMessages.map(m=> (
                      <tr key={m.id}><td>
                        <div><strong>{m.sender_type}</strong> <span style={{color:'#6b7280'}}>{new Date(m.created_at).toLocaleString()}</span></div>
                        {m.content && <div style={{marginTop:'0.25rem'}}>{m.content}</div>}
                        {m.media_urls && Array.isArray(m.media_urls) && m.media_urls.map((f,i)=> (<div key={i}><a className="download-link" href={f.url} target="_blank" rel="noreferrer">⬇️ {f.name}</a></div>))}
                      </td></tr>
                    ))}
                  </tbody></table>
                )}
              </div>
              <div className="form-row" style={{marginTop:'0.75rem'}}>
                <div className="form-group" style={{flex:1}}>
                  <input type="text" value={newChatMessage} onChange={(e)=> setNewChatMessage(e.target.value)} placeholder="Type a message" className="form-input" />
                </div>
                <div className="form-group">
                  <input type="file" multiple onChange={(e)=> setChatFiles(Array.from(e.target.files))} className="form-input" />
                </div>
                <div className="form-group">
                  <button className="btn-primary" onClick={sendChatMessage}>Send</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notices;
